import lodash from 'lodash';
import styles from './DescriptorEntry.scss';
import DataTypeEditor from 'common/components/DataTypeEditor/DataTypeEditor';
import AutocompleteDataSource from 'common/components/AutocompleteDataSource/AutocompleteDataSource';
import DataSourceValueLoader from 'common/containers/DataSourceValueLoader/DataSourceValueLoader';

const DescriptorEntry = ({
    editorValue,
    dataType,
    dataSourceId,
    dataset,
    changeEditorValue,
    markValueInvalid,
    onBlur,
    onFocus,
    onKeyDown,
    setupValues,
    singleValueDescriptorValues,
    selectedValues,
}) => {
    if (dataSourceId) {
        return <DataSourceDescriptorEntry dataSourceId={dataSourceId}
            onKeyDown={onKeyDown}
            editorValue={editorValue}
            dataType={dataType}
            onFocus={onFocus}
            onBlur={onBlur}
            markValueInvalid={markValueInvalid}
            changeEditorValue={changeEditorValue}
            setupValues={setupValues}
            selectedValues={selectedValues}
            singleValueDescriptorValues={singleValueDescriptorValues} />;
    } else if (dataset) {
        return <DatasetDescriptorEntry editorValue={editorValue}
            onKeyDown={onKeyDown}
            dataset={dataset}
            selectedValues={selectedValues}
            dataType={dataType}
            onFocus={onFocus}
            onBlur={onBlur}
            markValueInvalid={markValueInvalid}
            changeEditorValue={changeEditorValue} />;
    } else {
        return <NormalDescriptorEntry editorValue={editorValue}
            onKeyDown={onKeyDown}
            onFocus={onFocus}
            onBlur={onBlur}
            selectedValues={selectedValues}
            dataType={dataType}
            changeEditorValue={changeEditorValue} />;
    }
};

const DataSourceDescriptorEntry = ({
    editorValue,
    dataType,
    dataSourceId,
    changeEditorValue,
    markValueInvalid,
    onFocus,
    onBlur,
    setupValues,
    selectedValues,
    singleValueDescriptorValues,
}) => {
    const className = ['DATEONLY', 'DATETIME'].includes(dataType.toUpperCase())
        ? styles.descriptorEntryDate
        : styles.descriptorEntry;
    const onValidStateChange = (ds, { hasError, value }) => markValueInvalid(hasError);
    return (
        <DataSourceValueLoader dataSourceId={dataSourceId}>
            <ValuePicker value={editorValue}
                onFocus={onFocus}
                onBlur={onBlur}
                className={className}
                changeHandler={changeEditorValue}
                dataType={dataType}
                selectedValues={selectedValues}
                onValidStateChange={onValidStateChange}
                validate={true}
                setupValues={setupValues}
                singleValueDescriptorValues={singleValueDescriptorValues} />
        </DataSourceValueLoader>
    );
};

const DatasetDescriptorEntry = ({
    editorValue,
    dataset,
    changeEditorValue,
    markValueInvalid,
    onFocus,
    selectedValues,
    onBlur
}) => {
    const { values, dataType: descriptorDataType } = dataset;
    const dsValues = lodash.map(values, (value, index) => ({
        value: value,
        text: value,
        dataType: descriptorDataType,
        order: index
    }));
    const className = ['DATEONLY', 'DATETIME'].includes(descriptorDataType.toUpperCase())
        ? styles.descriptorEntryDate
        : styles.descriptorEntry;
    const onValidStateChange = (ds, { hasError }) => markValueInvalid(hasError);
    return (
        <AutocompleteDataSource values={dsValues}
            className={className}
            onChange={changeEditorValue}
            dataType={descriptorDataType}
            selectedValues={selectedValues}
            validate={true}
            onFocus={onFocus}
            onBlur={onBlur}
            onValidStateChange={onValidStateChange}
            value={editorValue}>
            <DataTypeEditor className={className} />
        </AutocompleteDataSource>
    );
};

const NormalDescriptorEntry = ({
    editorValue,
    dataType,
    changeEditorValue,
    onKeyDown,
    onFocus,
    onBlur
}) => {
    const className = ['DATEONLY', 'DATETIME'].includes(dataType.toUpperCase())
        ? styles.descriptorEntryDate
        : styles.descriptorEntry;
    return <DataTypeEditor value={editorValue}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        dataType={dataType}
        className={className}
        changeHandler={e => changeEditorValue(e.target.value, dataType)} />;
};

function filterWithSetupValues(
    setupValues,
    dataSourceItems,
    selectedValues,
    singleValueDescriptorValues,
    value,
) {
    // Build selected set
    const selectedSet = new Set(
        (Array.isArray(selectedValues) ? selectedValues : []).map(sv => {
            const val = sv && typeof sv === 'object' && 'value' in sv ? sv.value : sv;
            return String(val);
        })
    );

    const remainingItems = dataSourceItems.filter(
        item => !selectedSet.has(String(item.value))
    );

    const searchTerm = String(value || '').trim();
    const hasSetupValues = lodash.isArray(setupValues) && setupValues.length > 0;
    const setupSet = hasSetupValues ? new Set(setupValues.map(v => String(v))) : new Set();

    const remainingSetupItems = remainingItems.filter(item =>
        hasSetupValues ? setupSet.has(String(item.value)) : true
    );

    const remainingNonSetupItems = remainingItems.filter(item =>
        hasSetupValues ? !setupSet.has(String(item.value)) : true
    );

    // No typing → show setup values if exist, else all
    if (searchTerm === '') {
        return hasSetupValues ? remainingSetupItems : remainingItems;
    }

    const searchTermLower = searchTerm.toLowerCase();

    // For setup values → allow normal autocomplete (contains)
    const setupMatches = remainingSetupItems.filter(item =>
        item.value.toLowerCase().includes(searchTermLower)
    );

    // For non-setup values → allow only exact matches
    const nonSetupExactMatches = remainingNonSetupItems.filter(item =>
        item.value.toLowerCase() === searchTermLower
    );

    // Combine both groups
    const combinedResults = [...setupMatches, ...nonSetupExactMatches];

    // If still nothing matched, fallback to global exact match
    if (combinedResults.length === 0) {
        const fallback = remainingItems.filter(
            item => item.value.toLowerCase() === searchTermLower
        );
        return fallback;
    }
    return combinedResults;
}


const ValuePicker = ({
    dataSourceItems,
    className,
    changeHandler,
    dataType,
    value,
    validate,
    onValidStateChange,
    onKeyDown,
    onFocus,
    onBlur,
    setupValues,
    selectedValues,
    singleValueDescriptorValues,
}) => {
    
    const verifiedDataSourceItems = filterWithSetupValues(
        setupValues,
        dataSourceItems,
        selectedValues,
        singleValueDescriptorValues,
        value,
    );
    return (
        <AutocompleteDataSource values={verifiedDataSourceItems}
            onChange={changeHandler}
            dataType={dataType}
            validate={validate}
            selectedValues={selectedValues}
            onFocus={onFocus}
            onBlur={onBlur}
            onValidStateChange={onValidStateChange}
            value={value || ''}>
            <DataTypeEditor className={className}
                onKeyDown={onKeyDown} />
        </AutocompleteDataSource>
    );
};

export default DescriptorEntry;
